import { NavLink, Outlet, Navigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Settings2 } from "lucide-react";
import { useResearchHubAccess } from "@/hooks/useSurfaceAccess";
import { EmptyState, LoadingState } from "@/components/research-hub";
import { DataHealthBadge } from "@/components/research-hub/DataHealthBadge";

type Tab = { to: string; label: string };

// The teal reader shell. Mirrors ResearchLayout's grouped-tab conventions but with a small,
// task-oriented, surface-gated tab set and a persistent route to Ask on every screen.
export default function ResearchHubLayout() {
  const access = useResearchHubAccess();

  if (access.loading) {
    return (
      <div className="w-full px-4 py-6">
        <LoadingState rows={3} />
      </div>
    );
  }

  if (!access.canRead) {
    return (
      <div className="w-full px-4 py-10 max-w-2xl mx-auto">
        <EmptyState
          title="You don't have access to the research hub yet"
          body="Speak to the investment team if you think you should have access."
        />
      </div>
    );
  }

  const tabs: Tab[] = [
    ...(access.seeCio ? [{ to: "/partner/research-hub/today", label: "Today" }] : []),
    ...(access.seePm ? [{ to: "/partner/research-hub/book", label: "Book" }] : []),
    ...(access.seeAnalyst ? [{ to: "/partner/research-hub/workbench", label: "Workbench" }] : []),
    { to: "/partner/research-hub/ask", label: "Ask" },
    { to: "/partner/research-hub/briefs", label: "My briefs" },
    // Both routes were fully built but had no entry anywhere in the product — reachable only by
    // typing the URL. Deliberately UNGATED here to match their route definitions exactly
    // (researchRoutes.tsx:173-174 mount them with no <SurfaceGate>, unlike today/book/workbench):
    // a nav entry that is more permissive than its route sends people to a blank gate, and one
    // that is stricter hides a page they are entitled to.
    { to: "/partner/research-hub/meeting-notes", label: "Meeting notes" },
    { to: "/partner/research-hub/memory", label: "Memory" },
  ];

  return (
    <div className="w-full px-4 py-6 space-y-6 font-body">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary">Research hub</h1>
          <p className="text-muted-foreground text-sm">
            What needs your attention, how good our calls have been, and answers on demand.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DataHealthBadge isAdmin={access.isAdmin} />
          {access.isAdmin && (
            <NavLink
              to="/partner/research"
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-body text-[11px] text-muted-foreground hover:bg-muted transition-colors"
            >
              <Settings2 className="h-3.5 w-3.5" aria-hidden /> Ops console
            </NavLink>
          )}
        </div>
      </div>

      <nav className="flex gap-1 flex-wrap border-b pb-2">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            className={({ isActive }) =>
              cn(
                "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )
            }
          >
            {t.label}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </div>
  );
}

// Default-landing redirect: Today → Book → Workbench → Ask (first the user can see).
export function ResearchHubIndex() {
  const access = useResearchHubAccess();
  if (access.loading) {
    return (
      <div className="w-full px-4 py-6">
        <LoadingState rows={2} />
      </div>
    );
  }
  if (!access.canRead) return <Navigate to="/partner/research-hub/ask" replace />;
  const target = access.seeCio
    ? "today"
    : access.seePm
      ? "book"
      : access.seeAnalyst
        ? "workbench"
        : "ask";
  return <Navigate to={`/partner/research-hub/${target}`} replace />;
}
